import React, { FC, ReactElement, RefObject } from 'react';
import { Animate } from '@fiverr-private/transition';
import { renderChildren } from '@fiverr-private/ui_utils';
import { theme } from '@fiverr-private/theme';
import { EASING } from '@fiverr-private/transition/src/constants';

type DurationType = keyof typeof theme.animationDuration;

export type EasingType = keyof typeof EASING;

export const getTiming = (
  duration: DurationType = 'moderate1',
  easing: EasingType = 'easeIn',
  delay?: DurationType
) => ({
  duration: theme.numericAnimationDurations[duration],
  ease: EASING[easing],
  delay: theme.numericAnimationDurations[delay as any],
});

const animations = {
  enter: ({ delay, duration, easing }) => ({
    opacity: [1],
    x: [0],
    y: [0],
    timing: getTiming(duration, easing, delay),
  }),
  exit: ({ offsetX = 0, offsetY = 0, delay, duration, easing }) => ({
    opacity: [0],
    x: [offsetX],
    y: [offsetY],
    timing: getTiming(duration, easing, delay),
  }),
};

interface CustomSlideFadeProps {
  offsetX?: number;
  offsetY?: number;
  delay?: DurationType;
  duration?: DurationType;
  easing?: EasingType;
  show: boolean;
  onAnimationEnd?: () => void;
  children: ReactElement & { ref?: RefObject<HTMLElement> };
}
export const CustomSlideFade: FC<CustomSlideFadeProps> = ({
  offsetX = 0,
  offsetY = 0,
  delay,
  duration = 'moderate1',
  easing = 'easeIn',
  show,
  onAnimationEnd,
  children,
}) => {
  const custom = { offsetX, offsetY, delay, duration, easing };
  const transitionConfig = animations[show ? 'enter' : 'exit'](custom);

  return (
    <Animate
      show={show}
      start={animations.exit(custom)}
      enter={{
        ...transitionConfig,
        end: onAnimationEnd,
      }}
      update={transitionConfig}
      leave={{
        ...animations.exit(custom),
      }}
    >
      {({ x, y, ...rest }) =>
        renderChildren(children, {
          style: {
            ...(children?.props.style || {}),
            ...rest,
            display: rest.opacity === 0 || !show ? 'none' : undefined,
            transform: show ? `translate(${x}px,${y}px)` : undefined,
          },
        })
      }
    </Animate>
  );
};
